import { Children, forwardRef, type ReactNode } from "react";
import { cva } from "class-variance-authority";
import type { VariantProps } from "class-variance-authority";
import { cn } from "@/lib/cn";

/**
 * Button variants — Tahoe capsule recipe (approved 2026-06-10), revised by the
 * compact surface standard (2026-09-24, §6 and §10): icon sizes are round,
 * `secondary` is one soft fill with no hairline or inner shadow, and a
 * pressed ghost or secondary button shows the accent text only.
 *
 * .pen primitives mapped:
 *   ui/button-primary       → variant="default"
 *   ui/button-secondary     → variant="secondary"
 *   ui/button-ghost         → variant="ghost"
 *   ui/button-danger        → variant="destructive"
 *   ui/button-small-*       → size="sm"
 *   ui/button-icon-ghost    → variant="ghost"  + size="icon" | "iconSm"
 *   ui/button-icon-accent   → variant="default" + size="icon"
 *
 * Capsules have a fixed height, so a label never wraps: a button stays within
 * its container (`max-w-full`) and a plain-text label ends in an ellipsis
 * when a longer translation does not fit. Icon sizes never shrink.
 */
const buttonVariants = cva(
  "inline-flex max-w-full items-center justify-center whitespace-nowrap font-medium transition-all focus:outline-none focus-visible:outline-none focus-visible:ring-0 active:brightness-90 disabled:pointer-events-none disabled:opacity-40",
  {
    variants: {
      variant: {
        /* ui/button-primary: accent fill, black text, glow shadow.
         * `border border-transparent` keeps the box model identical to the
         * secondary variant so stacked primary+secondary buttons render at
         * pixel-identical widths. */
        default:
          "border border-transparent bg-accent text-black shadow-glow-accent hover:brightness-110",
        /* Neutral button: one soft fill in every theme, no hairline, no inner
         * shadow (compact surface standard §6, §10.3). */
        secondary:
          "border border-transparent bg-[var(--color-surface-selected)] text-primary hover:brightness-110 aria-[pressed=true]:text-accent",
        /* ui/button-ghost: invisible at rest, text brightens on hover */
        ghost:
          "text-muted transition-colors hover:text-primary aria-[pressed=true]:text-accent",
        /* ui/button-danger: transparent tint, danger border, danger-light text */
        destructive:
          "border border-btn-danger-stroke bg-btn-danger-fill text-danger-light shadow-btn-danger backdrop-blur-glass-sm hover:brightness-110",
        /* Unstyled link variant */
        link: "text-accent underline-offset-4 hover:underline",
      },
      size: {
        /* Tahoe capsule: airier control height, pill radius, body text */
        default:
          "h-[36px] gap-appkit-xs rounded-pill px-[18px] text-appkit-body",
        /* Tahoe small capsule: compact control height, pill radius, caption text */
        sm: "h-[28px] gap-appkit-2xs rounded-pill px-appkit-sm text-appkit-caption",
        /* Large: auth-page sized */
        lg: "h-12 gap-appkit-xs rounded-pill px-appkit-lg text-appkit-body",
        /* ui/button-icon-*: 36x36 capsule */
        icon: "h-[36px] w-[36px] shrink-0 rounded-pill",
        /* Icon button: 24px round (compact surface standard §6, §10.4) */
        iconSm: "h-appkit-lg w-appkit-lg shrink-0 rounded-pill",
        /* Cursor bubble controls: 16px round */
        iconXs: "h-4 w-4 shrink-0 rounded-pill",
        /* Connection chip (Settings › Browsers): 22px, small radius, hairline */
        chip: "h-[22px] gap-appkit-2xs rounded-control-sm border border-glass-stroke-subtle px-appkit-xs",
        /* Sidebar tab: shared control sizing with pill radius */
        tab: "h-[34px] gap-appkit-xs rounded-pill px-appkit-sm text-appkit-small",
      },
    },
    compoundVariants: [
      /* Small ghost: icon brightens on hover — no border, no background */
      {
        variant: "ghost",
        size: "sm",
        className: "",
      },
      /* Small danger: lighter fill, no border */
      {
        variant: "destructive",
        size: "sm",
        className: "border-transparent bg-btn-danger-fill-sm shadow-none",
      },
      /* Small primary: smaller glow */
      {
        variant: "default",
        size: "sm",
        className: "shadow-glow-accent-sm",
      },
      /* Icon ghost: icon brightens on hover — no border, no background */
      {
        variant: "ghost",
        size: "icon",
        className: "",
      },
      /* Icon button: its round shape lights up on hover and keyboard focus */
      {
        variant: "ghost",
        size: "iconSm",
        className:
          "hover:bg-[var(--color-interactive-hover)] focus-visible:bg-[var(--color-interactive-hover)]",
      },
      /* Chip: its shape lights up on hover, rings on keyboard focus */
      {
        variant: "ghost",
        size: "chip",
        className:
          "hover:bg-[var(--color-interactive-hover)] focus-visible:ring-2 focus-visible:ring-accent data-[state=open]:text-primary",
      },
      /* Icon accent: stronger glow (pill shape inherited from size) */
      {
        variant: "default",
        size: "icon",
        className: "shadow-glow-accent-icon",
      },
    ],
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);
type ButtonProps = React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    "data-testid"?: string;
  };

const isText = (child: ReactNode): child is string | number =>
  typeof child === "string" || typeof child === "number";

/** Wraps each plain-text run of the children in a span so a label too long
 *  for the button truncates; whitespace between an icon and its label stays
 *  a collapsible text node. */
function truncatingLabel(children: ReactNode): ReactNode[] {
  const content: ReactNode[] = [];
  let runs = 0;
  let run: (string | number)[] = [];
  const flush = () => {
    const text = run.join("");
    if (text.trim()) {
      content.push(
        <span key={`label-${runs++}`} className="min-w-0 truncate">
          {text}
        </span>,
      );
    } else if (text) content.push(text);
    run = [];
  };
  for (const child of Children.toArray(children)) {
    if (isText(child)) run.push(child);
    else {
      flush();
      content.push(child);
    }
  }
  flush();
  return content;
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, children, ...props }, ref) => {
    return (
      <button
        ref={ref}
        className={cn(buttonVariants({ variant, size, className }))}
        {...props}
      >
        {truncatingLabel(children)}
      </button>
    );
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
export type { ButtonProps };
